import React, { useState, useEffect } from 'react'
import { userJourneyAPI } from '../services/userJourneyService'
import AstrologicalChartDisplay from './AstrologicalChartDisplay'

interface UserJourneyData {
  analytics: any
  userProfile: any
  userInsights: any
  userInteractions: any[]
  chartData?: any
}

const UserJourneyDashboard: React.FC = () => {
  const [journeyData, setJourneyData] = useState<UserJourneyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [testUserId] = useState('test-user-123')

  useEffect(() => {
    loadUserJourneyData()
  }, [])

  const loadUserJourneyData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Load analytics
      const analyticsResponse = await userJourneyAPI.getAnalytics()
      
      // Load user profile (if exists)
      const profileResponse = await userJourneyAPI.getUserProfile(testUserId)
      
      // Load user insights (if exists)
      const insightsResponse = await userJourneyAPI.getUserInsights(testUserId)
      
      // Load user interactions (if exists)
      const interactionsResponse = await userJourneyAPI.getUserInteractions(testUserId, 10)

      setJourneyData({
        analytics: analyticsResponse.success ? analyticsResponse.analytics : null,
        userProfile: profileResponse.success ? profileResponse.profile?.profile || profileResponse.profile : null,
        userInsights: insightsResponse.success ? insightsResponse.insights?.insights || insightsResponse.insights : null,
        userInteractions: interactionsResponse.success ? interactionsResponse.interactions?.interactions || interactionsResponse.interactions : [],
        chartData: profileResponse.success && profileResponse.profile?.profile?.chartData ? profileResponse.profile.profile.chartData : null
      })
    } catch (err) {
      setError('Failed to load user journey data')
      console.error('Error loading user journey data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleTestSignup = async () => {
    try {
      const signupData = {
        email: 'test@example.com',
        name: 'Test User',
        dateOfBirth: '1990-01-01',
        timeOfBirth: '14:30',
        placeOfBirth: 'New York, NY',
        gender: 'male'
      }

      const response = await userJourneyAPI.signup(signupData)
      if (response.success) {
        alert('Test user created successfully!')
        loadUserJourneyData()
      } else {
        alert('Failed to create test user: ' + response.error)
      }
    } catch (err) {
      alert('Error creating test user')
      console.error('Error creating test user:', err)
    }
  }

  const handleTestInteraction = async () => {
    try {
      const chatData = {
        userId: testUserId,
        message: 'I need help with stress management',
        response: 'Here are some yoga poses for stress relief',
        context: { topic: 'stress', intent: 'help' }
      }

      const response = await userJourneyAPI.trackChatInteraction(chatData)
      if (response.success) {
        alert('Test interaction tracked successfully!')
        loadUserJourneyData()
      } else {
        alert('Failed to track interaction: ' + response.error)
      }
    } catch (err) {
      alert('Error tracking interaction')
      console.error('Error tracking interaction:', err)
    }
  }

  const handleTestChartGeneration = async () => {
    try {
      const chartData = {
        dateOfBirth: '1990-01-01',
        timeOfBirth: '14:30',
        placeOfBirth: 'New York, NY'
      }

      const response = await fetch('/api/user-journey/test-chart-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chartData)
      })

      const result = await response.json()
      if (result.success) {
        alert('Chart data generated successfully! Check the console for details.')
        console.log('Generated chart data:', result.chartData)
        loadUserJourneyData()
      } else {
        alert('Failed to generate chart data: ' + result.error)
      }
    } catch (err) {
      alert('Error generating chart data')
      console.error('Error generating chart data:', err)
    }
  }

  const handleTestComprehensiveCharts = async () => {
    try {
      const chartData = {
        dateOfBirth: '1990-01-01',
        timeOfBirth: '14:30',
        placeOfBirth: 'New York, NY'
      }

      const response = await fetch('/api/user-journey/test-comprehensive-charts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(chartData)
      })

      const result = await response.json()
      if (result.success) {
        alert(`Comprehensive chart data generated successfully!\nAvailable charts: ${result.availableCharts.join(', ')}`)
        console.log('Generated comprehensive chart data:', result.chartData)
        loadUserJourneyData()
      } else {
        alert('Failed to generate comprehensive chart data: ' + result.error)
      }
    } catch (err) {
      alert('Error generating comprehensive chart data')
      console.error('Error generating comprehensive chart data:', err)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading User Journey Data...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4">User Journey Dashboard</h1>
          <p className="text-purple-200 text-lg">Track and analyze user interactions with AUM AI</p>
        </div>

        {error && (
          <div className="bg-red-500 text-white p-4 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Test Actions */}
        <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold text-white mb-4">Test Actions</h2>
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={handleTestSignup}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Create Test User
            </button>
            <button
              onClick={handleTestInteraction}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Track Test Interaction
            </button>
            <button
              onClick={loadUserJourneyData}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Refresh Data
            </button>
            <button
              onClick={handleTestChartGeneration}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Test Chart Generation
            </button>
            <button
              onClick={handleTestComprehensiveCharts}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Test Comprehensive Charts
            </button>
            <button
              onClick={() => {
                alert('UserJourneyDashboard button click is working!')
                console.log('UserJourneyDashboard test button clicked')
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Test Click
            </button>
          </div>
        </div>

        {/* Analytics Section */}
        {journeyData?.analytics && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-white mb-4">System Analytics</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-purple-600/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-2">Total Users</h3>
                <p className="text-3xl font-bold text-purple-300">{journeyData.analytics.totalUsers}</p>
              </div>
              <div className="bg-blue-600/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-2">Total Interactions</h3>
                <p className="text-3xl font-bold text-blue-300">{journeyData.analytics.totalInteractions}</p>
              </div>
              <div className="bg-green-600/20 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-2">Avg Interactions/User</h3>
                <p className="text-3xl font-bold text-green-300">
                  {journeyData.analytics.averageInteractionsPerUser 
                    ? journeyData.analytics.averageInteractionsPerUser.toFixed(2) 
                    : '0.00'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* User Profile Section */}
        {journeyData?.userProfile && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-white mb-4">User Profile</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Basic Information</h3>
                <div className="space-y-2 text-purple-200">
                  <p><span className="font-semibold">Name:</span> {journeyData.userProfile.name}</p>
                  <p><span className="font-semibold">Email:</span> {journeyData.userProfile.email}</p>
                  <p><span className="font-semibold">Date of Birth:</span> {journeyData.userProfile.dateOfBirth}</p>
                  <p><span className="font-semibold">Place of Birth:</span> {journeyData.userProfile.placeOfBirth}</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Profile Details</h3>
                <div className="space-y-2 text-purple-200">
                  <p><span className="font-semibold">Created:</span> {new Date(journeyData.userProfile.createdAt).toLocaleDateString()}</p>
                  <p><span className="font-semibold">Updated:</span> {new Date(journeyData.userProfile.updatedAt).toLocaleDateString()}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Astrological Chart Data Section */}
        {journeyData?.chartData && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-white mb-4">Astrological Chart Data</h2>
            <AstrologicalChartDisplay chartData={journeyData.chartData} />
          </div>
        )}

        {/* User Insights Section */}
        {journeyData?.userInsights && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-white mb-4">User Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Personality Traits</h3>
                <div className="flex flex-wrap gap-2">
                  {journeyData.userInsights.personalityTraits?.map((trait: string, index: number) => (
                    <span key={index} className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm">
                      {trait}
                    </span>
                  )) || <span className="text-purple-200">No personality traits available</span>}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Wellness Needs</h3>
                <div className="flex flex-wrap gap-2">
                  {journeyData.userInsights.wellnessNeeds?.map((need: string, index: number) => (
                    <span key={index} className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm">
                      {need}
                    </span>
                  )) || <span className="text-purple-200">No wellness needs available</span>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Interactions Section */}
        {journeyData?.userInteractions && Array.isArray(journeyData.userInteractions) && journeyData.userInteractions.length > 0 && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-white mb-4">Recent Interactions</h2>
            <div className="space-y-4">
              {journeyData.userInteractions.slice(0, 5).map((interaction: any, index: number) => (
                <div key={index} className="bg-white/5 p-4 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm">
                      {interaction.type || interaction.interactionType}
                    </span>
                    <span className="text-purple-200 text-sm">
                      {new Date(interaction.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-white mb-2">
                    <span className="font-semibold">Content:</span> {interaction.content || interaction.input?.message || 'No content'}
                  </p>
                  {interaction.topics && interaction.topics.length > 0 && (
                    <p className="text-purple-200 mb-1">
                      <span className="font-semibold">Topics:</span> {interaction.topics.join(', ')}
                    </p>
                  )}
                  {interaction.emotions && interaction.emotions.length > 0 && (
                    <p className="text-purple-200">
                      <span className="font-semibold">Emotions:</span> {interaction.emotions.join(', ')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* No Data Message */}
        {!journeyData?.analytics && !journeyData?.userProfile && !journeyData?.userInsights && (
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 text-center">
            <h2 className="text-2xl font-semibold text-white mb-4">No User Journey Data</h2>
            <p className="text-purple-200 mb-4">
              Create a test user and track some interactions to see the user journey data here.
            </p>
            <button
              onClick={handleTestSignup}
              className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
            >
              Create Test User
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default UserJourneyDashboard 